import type { ObjectId } from "@bbj-types/utils";
import { CheckboxStatus } from "@root/utils/enums/checkbox";
import { type SyntheticEvent, useCallback, useEffect, useState } from "react";
import type { IAutocompleteAppellationsProps } from "../index";
import type { TAutocompleteAppellationsOption } from "./options";

export type TAutocompleteAppellationsSelectedOption = TAutocompleteAppellationsOption & { selected: boolean };
export type TAutocompleteAppellationsSelectedOptionsGroups = Record<
  string,
  {
    label: string;
    options: TAutocompleteAppellationsSelectedOption[];
    optionsSet: Set<ObjectId>;
  }
>;

const formatValueToSelectedOptionsGroups = (value: IAutocompleteAppellationsProps["value"]) => {
  const selectedOptionsGroups: TAutocompleteAppellationsSelectedOptionsGroups = {};
  for (const option of value) {
    if (selectedOptionsGroups[option.rome] === undefined) {
      selectedOptionsGroups[option.rome] = {
        label: option.romeLabel,
        options: [{ ...option, selected: true }],
        optionsSet: new Set([option._id]),
      };
    } else {
      selectedOptionsGroups[option.rome].options.push({ ...option, selected: true });
      selectedOptionsGroups[option.rome].optionsSet.add(option._id);
    }
  }
  return selectedOptionsGroups;
};

const updateSelectedOptionsSelection = (
  valueSet: Set<ObjectId>,
  selectedOptionsGroups: TAutocompleteAppellationsSelectedOptionsGroups
) => {
  const newSelectedOptionsGroups: TAutocompleteAppellationsSelectedOptionsGroups = {};
  for (const group in selectedOptionsGroups) {
    const newOptions: TAutocompleteAppellationsSelectedOption[] = [];
    const newOptionsSet = new Set<ObjectId>();
    for (const option of selectedOptionsGroups[group].options) {
      const selected = valueSet.has(option._id);
      newOptions.push({ ...option, selected });
      if (selected) {
        newOptionsSet.add(option._id);
      }
    }
    newSelectedOptionsGroups[group] = {
      ...selectedOptionsGroups[group],
      options: newOptions,
      optionsSet: newOptionsSet,
    };
  }
  return newSelectedOptionsGroups;
};

export const useAutocompleteAppellationsSelected = (
  value: IAutocompleteAppellationsProps["value"],
  valueSet: Set<ObjectId>,
  onChange: IAutocompleteAppellationsProps["onChange"]
) => {
  const [selectedOptionsGroups, setSelectedOptionsGroups] = useState<TAutocompleteAppellationsSelectedOptionsGroups>(
    () => formatValueToSelectedOptionsGroups(value)
  );

  const handleAutocompleteClose = () => {
    setSelectedOptionsGroups(formatValueToSelectedOptionsGroups(value));
  };

  useEffect(() => {
    setSelectedOptionsGroups((selectedOptionsGroups) =>
      updateSelectedOptionsSelection(valueSet, selectedOptionsGroups)
    );
  }, [valueSet]);

  const handleClickSelectedOptionGroup = useCallback(
    (event: SyntheticEvent, group: string, groupStatus: CheckboxStatus) => {
      event.preventDefault();
      if (groupStatus === CheckboxStatus.CHECKED) {
        onChange(
          event,
          value.filter((option) => !selectedOptionsGroups[group].optionsSet.has(option._id)),
          "removeOption"
        );
      } else {
        const newValue = [...value];
        for (const option of selectedOptionsGroups[group].options) {
          if (!valueSet.has(option._id)) {
            newValue.push(option);
          }
        }
        onChange(event, newValue, "selectOption");
      }
    },
    [value, valueSet, onChange, selectedOptionsGroups]
  );

  const handleClickSelectedOption = useCallback(
    (event: SyntheticEvent, selectedOption: TAutocompleteAppellationsSelectedOption) => {
      event.preventDefault();
      if (selectedOption.selected) {
        onChange(
          event,
          value.filter((option) => option._id !== selectedOption._id),
          "removeOption"
        );
      } else {
        onChange(event, [...value, selectedOption], "selectOption");
      }
    },
    [value, onChange]
  );

  return {
    selectedOptionsGroups,
    handleAutocompleteClose,
    handleClickSelectedOptionGroup,
    handleClickSelectedOption,
  };
};
